import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { style: {"display":"flex"} }
const _hoisted_2 = { id: "map-container" }

import { ref, computed, nextTick, watch, provide } from "vue";
import {
  LMap,
  LTileLayer,
  LWmsTileLayer,
  LControlLayers,
  LPolygon,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { LatLng, LatLngBounds } from "leaflet";
import { FieldMask } from "@/modules/FieldMask";
import { Landmarks } from "@/modules/Landmarks";
import { useMoorings } from "@/modules/Moorings";
import LandmarkMarker from "./LandmarkMarker.vue";
import MooringMarker from "./MooringMarker.vue";
import ClearableInput from "./ClearableInput.vue";
import { MYMAP_KEY } from "@/types/symbols";


export default /*#__PURE__*/_defineComponent({
  __name: 'MooringMap',
  setup(__props) {

const mymap = ref<InstanceType<typeof LMap> | null>(null);
const ready = ref(false);
const dataLoaded = ref(false);

const root = document.documentElement;

const zoom = ref(16.5);
const mapCenter = ref([41.47601, -71.33095]);
const bounds = ref(
  new LatLngBounds(new LatLng(41.502, -71.2846), new LatLng(41.45, -71.3773))
);
const landmarks = ref(Landmarks);
const fieldMask = ref(FieldMask);
const search = ref("");

const boundsAsArray = computed(() => {
  const ne = bounds.value.getNorthEast();
  const sw = bounds.value.getSouthWest();
  return [
    [ne.lat, ne.lng],
    [sw.lat, sw.lng],
  ];
});

const { data: moorings, error } = useMoorings();

const mooringsLoaded = watch(moorings, () => {
  dataLoaded.value = true;
  if (ready.value) {
    initialMap();
  }
});

const onReady = async () => {
  root.classList.remove("chart");
  nextTick(() => {
    ready.value = true;
    if (dataLoaded.value) {
      initialMap();
    }
  });
};

async function initialMap() {
  const map = (mymap.value as any).leafletObject;
  const mapSize = map.getSize();
  if (mapSize.x === 0) {
    map.on("resize", (e: any) => {
      if (e.oldSize.x === 0) {
        initialZoom();
      }
    });
  } else {
    initialZoom();
  }

  map.on("baselayerchange", function (e: any) {
    if (e.name === "Chart") {
      root.classList.add("chart");
    } else {
      root.classList.remove("chart");
    }
  });
}

async function initialZoom() {
  const map = (mymap.value as any).leafletObject;
  if (map.listens("resize")) {
    map.off("resize");
  }

  const { latLngBounds } = await import("leaflet/dist/leaflet-src.esm");

  const [...landmarkCoords] = Landmarks.filter((l) => !l.navAid).map(
    (marker) => [marker.pos.lat, marker.pos.lng]
  );
  const [...mooringCoords] = moorings.value.map((marker) => [
    marker.pos.lat,
    marker.pos.lng,
  ]);
  const bounds = latLngBounds([...landmarkCoords, ...mooringCoords]).pad(0.1);

  map.fitBounds(bounds);
}
provide(MYMAP_KEY, mymap);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(LMap), {
        zoom: zoom.value,
        "onUpdate:zoom": _cache[1] || (_cache[1] = ($event: any) => ((zoom).value = $event)),
        center: mapCenter.value,
        minZoom: 13,
        maxZoom: 19,
        zoomSnap: 0.5,
        zoomDelta: 0.5,
        maxBounds: _unref(boundsAsArray),
        maxBoundsViscosity: 0.8,
        onReady: onReady,
        options: { useGlobalLeaflet: true },
        ref_key: "mymap",
        ref: mymap
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(LControlLayers), { position: "bottomleft" }),
          _createVNode(_unref(LWmsTileLayer), {
            "base-url": "https://gis.charttools.noaa.gov/arcgis/rest/services/MCS/NOAAChartDisplay/MapServer/exts/MaritimeChartService/WMSServer",
            attribution: "<a href='noaa.gov'>NOAA</a>",
            layers: "1,2,3,6",
            name: "Chart",
            "layer-type": "base",
            visible: false
          }),
          _createVNode(_unref(LTileLayer), {
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            attribution: "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
            name: "Satellite",
            "layer-type": "base",
            visible: true
          }),
          _createVNode(_unref(LPolygon), {
            "lat-lngs": fieldMask.value,
            color: "#011410",
            fill: true,
            fillOpacity: 1,
            fillColor: "#011410",
            className: "fieldmask"
          }, null, 8, ["lat-lngs"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(landmarks.value, (landmark) => {
            return (_openBlock(), _createBlock(LandmarkMarker, {
              key: landmark.name,
              landmark: landmark
            }, null, 8, ["landmark"]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(moorings), (mooring) => {
            return (_openBlock(), _createBlock(MooringMarker, {
              key: mooring.name,
              mooring: mooring,
              show: mooring.contains(search.value)
            }, null, 8, ["mooring", "show"]))
          }), 128)),
          _createVNode(ClearableInput, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            placeholder: "Search"
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }, 8, ["zoom", "center", "zoomSnap", "zoomDelta", "maxBounds", "maxBoundsViscosity"])
    ])
  ]))
}
}

})